/**
 * Module-level toast event bus.
 *
 * Toasts are dispatched from two worlds:
 *   - inside React (page action handlers via `useToast()`)
 *   - **outside** React (the axios response interceptor in
 *     `api/interceptors.ts`, which can't call hooks)
 *
 * A tiny pub/sub bus bridges both. `<ToastProvider>` subscribes on mount
 * and renders the queue; non-React callers (interceptors) `publish(...)`
 * directly. This keeps the interceptor decoupled from the React tree and
 * avoids a circular import (interceptor → provider → interceptor).
 */
export type ToastSeverity = 'success' | 'info' | 'warning' | 'error';

export interface ToastMessage {
  /** Stable id so React can key + dedupe rapid duplicates. */
  id: string;
  /** Pre-translated message text (callers resolve `t()` before publishing). */
  message: string;
  severity: ToastSeverity;
  /** Auto-hide delay in ms. `null` keeps it open until dismissed. */
  durationMs: number | null;
}

export type ToastInput = Omit<ToastMessage, 'id'> & { id?: string };

type ToastListener = (toast: ToastMessage) => void;

const listeners = new Set<ToastListener>();

let counter = 0;
const nextId = (): string => {
  counter += 1;
  return `toast-${counter}`;
};

/**
 * Subscribe to the bus. Returns an unsubscribe fn — `<ToastProvider>`
 * calls this in a `useEffect` and cleans up on unmount.
 */
export const subscribeToToasts = (listener: ToastListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Publish a toast to every subscriber. Safe to call from non-React code.
 */
export const publishToast = (input: ToastInput): void => {
  const toast: ToastMessage = {
    id: input.id ?? nextId(),
    message: input.message,
    severity: input.severity,
    durationMs: input.durationMs,
  };
  listeners.forEach((listener) => {
    listener(toast);
  });
};

/** Test-only: drop all subscribers so specs don't leak listeners. */
export const _resetToastBusForTests = (): void => {
  listeners.clear();
  counter = 0;
};
